import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Drawer,
  Stack,
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Tooltip,
  Chip,
  useTheme,
  useMediaQuery,
} from 'omaha-design-system';
import {
  X as CloseIcon,
  CaretDown as ExpandMoreIcon,
} from '@phosphor-icons/react';
import {
  LineupReportData,
  GameLineupReport,
  TeamLineup,
  LineupPlayer,
} from '../hooks/useLineupReport';
import BattingOrderBadge from './BattingOrderBadge';

interface MLBLineupsModalProps {
  open: boolean;
  onClose: () => void;
  data?: LineupReportData;
  isLoading?: boolean;
  defaultExpandedGameId?: string;
}

const LineupStatusChip: React.FC<{ confirmed: boolean }> = ({ confirmed }) => (
  <Chip
    label={confirmed ? 'Confirmed' : 'Pending'}
    size="small"
    sx={{
      backgroundColor: confirmed
        ? 'rgba(0, 181, 83, 0.16)'
        : 'rgba(170, 153, 0, 0.16)',
      color: confirmed ? '#00B553' : '#AA9900',
      fontFamily: '"Instrument Sans", sans-serif',
      fontWeight: 600,
      fontSize: '11.11px',
      height: 24,
      borderRadius: '4px',
      flexShrink: 0,
    }}
  />
);

const PlayerLineupRow: React.FC<{ player: LineupPlayer }> = ({
  player,
}) => {
  const injuryColor =
    player.injuryStatus?.toLowerCase() === 'out'
      ? '#EF5350'
      : player.injuryStatus?.toLowerCase() === 'doubtful'
      ? '#E57373'
      : player.injuryStatus?.toLowerCase() === 'questionable'
      ? '#FFA726'
      : player.injuryStatus?.toLowerCase() === 'probable'
      ? '#66BB6A'
      : '#9E9E9E';

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ py: 0.75 }}
    >
      <BattingOrderBadge battingOrder={player.battingOrder} confirmedLineup={player.confirmedLineup} size={20} />
      <Avatar
        src={player.avatar}
        alt={player.name}
        sx={{ width: 28, height: 28, flexShrink: 0 }}
      />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: '"Instrument Sans", sans-serif',
            fontSize: '13.33px',
            fontWeight: 600,
            color: '#F2F2F3',
            lineHeight: 1.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {player.name}
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Instrument Sans", sans-serif',
            fontSize: '11px',
            fontWeight: 500,
            color: 'rgba(242, 242, 243, 0.5)',
            flexShrink: 0,
          }}
        >
          {player.position}
          {player.hand && (
            <Box component="span" sx={{ ml: 0.5, color: 'rgba(242, 242, 243, 0.38)' }}>
              ({player.battingOrder === 0
                ? `${player.hand}HP`
                : player.hand})
            </Box>
          )}
        </Typography>
        {player.injuryStatus && (
          <Tooltip title={player.injuryNotes || player.injuryStatus} arrow>
            <Box
              sx={{
                px: 0.5,
                py: 0.125,
                borderRadius: '3px',
                backgroundColor: `${injuryColor}33`,
                fontFamily: '"Instrument Sans", sans-serif',
                fontSize: '9px',
                fontWeight: 700,
                color: injuryColor,
                lineHeight: 1.4,
                flexShrink: 0,
                textTransform: 'uppercase',
              }}
            >
              {player.injuryStatus}
            </Box>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};

const TeamLineupSection: React.FC<{
  team: TeamLineup;
}> = ({ team }) => {
  const hasBatters = team.batters.length > 0;

  return (
    <Stack sx={{ flex: 1 }}>

      {/* Starting Pitcher */}
      {team.startingPitcher && (
        <>
          <Typography
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              fontSize: '11px',
              fontWeight: 600,
              color: 'rgba(242, 242, 243, 0.38)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 0.5,
            }}
          >
            Starting Pitcher
          </Typography>
          <PlayerLineupRow player={team.startingPitcher} />
          <Box
            sx={{
              height: '1px',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              my: 1,
            }}
          />
        </>
      )}

      {/* Batting Order */}
      {hasBatters ? (
        <>
          <Typography
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              fontSize: '11px',
              fontWeight: 600,
              color: 'rgba(242, 242, 243, 0.38)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 0.5,
            }}
          >
            Batting Order
          </Typography>
          {team.batters.map((player) => (
            <PlayerLineupRow
              key={player.playerId}
              player={player}
            />
          ))}
        </>
      ) : (
        <Typography
          sx={{
            fontFamily: '"Instrument Sans", sans-serif',
            fontSize: '13.33px',
            color: 'rgba(242, 242, 243, 0.38)',
            fontStyle: 'italic',
            py: 1,
          }}
        >
          Lineup not yet available
        </Typography>
      )}
    </Stack>
  );
};

const GameAccordion: React.FC<{
  game: GameLineupReport;
  isMobile: boolean;
  defaultExpanded?: boolean;
}> = ({ game, isMobile, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        disableGutters
        sx={{
          backgroundColor: '#212223',
          borderRadius: '8px !important',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: '0 !important' },
          margin: '0 !important',
          boxShadow: '0px 1px 2px rgba(0,0,0,0.02)',
        }}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon size={24} color="rgba(242, 242, 243, 0.7)" />
          }
          sx={{
            px: 2,
            minHeight: 56,
            '&.Mui-expanded': { minHeight: 56 },
            '& .MuiAccordionSummary-content': {
              margin: '12px 0',
              '&.Mui-expanded': { margin: '12px 0' },
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%', pr: 1 }}
          >
            {/* Away team + status — left side */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              <Box
                component="img"
                src={game.awayTeam.logo}
                alt={game.awayTeam.abbr}
                sx={{ width: 24, height: 24, borderRadius: '4px', flexShrink: 0 }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <Typography
                noWrap
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '13.33px',
                  fontWeight: 600,
                  color: '#F2F2F3',
                }}
              >
                {isMobile ? game.awayTeam.abbr : game.awayTeam.name}
              </Typography>
              <LineupStatusChip confirmed={game.awayTeam.confirmed} />
            </Stack>

            {/* Center — @ and time */}
            <Stack alignItems="center" sx={{ flexShrink: 0, px: 1.5 }}>
              <Typography
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '13.33px',
                  fontWeight: 400,
                  color: 'rgba(242, 242, 243, 0.7)',
                }}
              >
                @
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '11px',
                  fontWeight: 400,
                  color: 'rgba(242, 242, 243, 0.5)',
                  lineHeight: 1,
                }}
              >
                {game.gameTime}
              </Typography>
            </Stack>

            {/* Home team + status — right side */}
            <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end" sx={{ flex: 1, minWidth: 0 }}>
              <LineupStatusChip confirmed={game.homeTeam.confirmed} />
              <Typography
                noWrap
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '13.33px',
                  fontWeight: 600,
                  color: '#F2F2F3',
                }}
              >
                {isMobile ? game.homeTeam.abbr : game.homeTeam.name}
              </Typography>
              <Box
                component="img"
                src={game.homeTeam.logo}
                alt={game.homeTeam.abbr}
                sx={{ width: 24, height: 24, borderRadius: '4px', flexShrink: 0 }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            pl: 2,
            pr: { xs: 2, md: 6 }, // extra right padding on desktop to align divider with header center
            pt: 1,
            pb: 2,
            backgroundColor: '#212223',
          }}
        >
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={isMobile ? 2 : 3}
          >
            <TeamLineupSection team={game.awayTeam} />

            {/* Divider */}
            {!isMobile && (
              <Box
                sx={{
                  width: '1px',
                  alignSelf: 'stretch',
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  flexShrink: 0,
                }}
              />
            )}
            {isMobile && (
              <Box
                sx={{
                  height: '1px',
                  width: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                }}
              />
            )}

            <TeamLineupSection team={game.homeTeam} />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const LineupsContent: React.FC<{
  data?: LineupReportData;
  isLoading?: boolean;
  isMobile: boolean;
  defaultExpandedGameId?: string;
}> = ({ data, isLoading, isMobile, defaultExpandedGameId }) => (
  <Stack spacing="12px">
    {data?.games.map((game) => (
      <GameAccordion
        key={game.gameId}
        game={game}
        isMobile={isMobile}
        defaultExpanded={game.gameId === defaultExpandedGameId}
      />
    ))}
    {(!data || data.games.length === 0) && (
      <Typography
        sx={{
          fontFamily: '"Instrument Sans", sans-serif',
          fontSize: '16px',
          color: 'rgba(242, 242, 243, 0.7)',
          textAlign: 'center',
          py: 4,
        }}
      >
        {isLoading ? 'Loading lineups...' : 'No lineup data available'}
      </Typography>
    )}
  </Stack>
);

const SummaryChips: React.FC<{ data?: LineupReportData }> = ({ data }) => {
  if (!data) return null;
  return (
    <Stack direction="row" spacing={2}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#00B553',
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontFamily: '"Instrument Sans", sans-serif',
            fontSize: '11.11px',
            fontWeight: 500,
            color: '#F2F2F3',
            whiteSpace: 'nowrap',
          }}
        >
          Confirmed ({data.confirmedCount})
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#AA9900',
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontFamily: '"Instrument Sans", sans-serif',
            fontSize: '11.11px',
            fontWeight: 500,
            color: '#F2F2F3',
            whiteSpace: 'nowrap',
          }}
        >
          Pending ({data.pendingCount})
        </Typography>
      </Stack>
    </Stack>
  );
};

export const MLBLineupsModal: React.FC<MLBLineupsModalProps> = ({
  open,
  onClose,
  data,
  isLoading,
  defaultExpandedGameId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Mobile: Bottom sheet (Drawer)
  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            backgroundColor: '#343536',
            borderRadius: '16px 16px 0 0',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            maxHeight: '90vh',
            boxShadow: '0px -5px 10px rgba(0,0,0,0.22)',
          },
        }}
      >
        <Stack sx={{ height: '100%', overflow: 'hidden' }}>
          {/* Grabber and close */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              pt: 1,
              px: 2,
            }}
          >
            <Box />
            <Box
              sx={{
                width: 40,
                height: 4,
                backgroundColor: 'rgba(255, 255, 255, 0.16)',
                borderRadius: '8px',
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                onClick={onClose}
                aria-label="Close"
                sx={{ color: 'rgba(242, 242, 243, 0.7)', p: 1 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Header */}
          <Box sx={{ px: 2, pb: 2 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Stack spacing={0.5}>
                <Typography
                  sx={{
                    fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                    fontWeight: 900,
                    fontSize: '19.2px',
                    color: '#F2F2F3',
                  }}
                >
                  MLB Lineups
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Instrument Sans", sans-serif',
                    fontWeight: 600,
                    fontSize: '13.33px',
                    color: 'rgba(242, 242, 243, 0.7)',
                  }}
                >
                  {data?.games.length || 0} games on today's slate
                </Typography>
              </Stack>
              <Stack alignItems="flex-end" spacing={0.5}>
                <Typography
                  sx={{
                    fontFamily: '"Instrument Sans", sans-serif',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#F2F2F3',
                    lineHeight: 1.5,
                  }}
                >
                  {data?.date || 'Loading...'}
                </Typography>
              </Stack>
            </Stack>

            <Box sx={{ mt: 2 }}>
              <SummaryChips data={data} />
            </Box>
          </Box>

          <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.12)' }} />

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
            <LineupsContent
              data={data}
              isLoading={isLoading}
              isMobile={isMobile}
              defaultExpandedGameId={defaultExpandedGameId}
            />
          </Box>
        </Stack>
      </Drawer>
    );
  }

  // Desktop: Dialog
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#2A2B2C',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Stack spacing={0.5}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
              fontWeight: 900,
              color: '#F2F2F3',
            }}
          >
            MLB Lineups
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              fontWeight: 600,
              fontSize: '16px',
              color: 'rgba(242, 242, 243, 0.7)',
            }}
          >
            {data?.games.length || 0} games on today's slate
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Stack alignItems="flex-end" spacing={0}>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontSize: '16px',
                fontWeight: 600,
                color: '#F2F2F3',
                lineHeight: 1.5,
              }}
            >
              {data?.date || 'Loading...'}
            </Typography>
          </Stack>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{ color: 'rgba(242, 242, 243, 0.7)', p: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Box sx={{ px: 3, pb: 2 }}>
        <SummaryChips data={data} />
      </Box>

      <DialogContent
        sx={{
          px: 3,
          py: 2,
          backgroundColor: '#242526',
          borderTop: '1px solid rgba(255, 255, 255, 0.12)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        }}
      >
        <LineupsContent
          data={data}
          isLoading={isLoading}
          isMobile={isMobile}
          defaultExpandedGameId={defaultExpandedGameId}
        />
      </DialogContent>
    </Dialog>
  );
};

export default MLBLineupsModal;
